import { EnvironmentClassSelect } from "@/components/EnvironmentClassSelect";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { NO_PROJECT_SELECTED } from "@/components/projects/ProjectSelect";
import { sortEnvironmentClassEntries, useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { useEnvironmentClassSelection } from "@/hooks/use-environment-class-selection";
import { useCreateEnvironment } from "@/queries/environment-queries";
import { EnvironmentCreateError } from "@/routes/environments/create/components/EnvironmentCreateError";
import { selectedClassEntryAtom } from "@/routes/environments/create/create-environment-utils";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import { useAtom } from "jotai";
import { type FC, type FormEvent, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const BlankTab: FC<{
    onClose: () => void;
}> = ({ onClose }) => {
    const [selectedClassEntry, setSelectedClassEntry] = useAtom(selectedClassEntryAtom);
    const navigate = useNavigate();
    const { toast } = useToast();
    const [createError, setCreateError] = useState<Error | null>(null);
    const isSubmittingRef = useRef(false);

    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();
    const sortedEnvironmentClassEntries = useMemo(
        () => sortEnvironmentClassEntries(environmentClassEntries),
        [environmentClassEntries],
    );

    useEnvironmentClassSelection({
        isLoadingEnvClasses,
        selectedClassEntry,
        sortedEnvironmentClassEntries,
        setSelectedClassEntry,
        selectedProject: NO_PROJECT_SELECTED,
        isValidContext: true, // there is no context to validate here
    });

    const createEnvironment = useCreateEnvironment();

    const handleContinue = useCallback(async () => {
        // Prevent duplicate submissions
        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        try {
            setCreateError(null);
            const environment = await createEnvironment.mutateAsync({
                type: "blank",
                classID: selectedClassEntry?.clazz?.id ?? "",
            });
            onClose();
            navigate(getDetailsURL({ environment }));
        } catch (error) {
            setCreateError(error instanceof Error ? error : new Error(String(error)));
            toast({ title: "Failed to create an environment", description: formatError(error), type: "error", duration: 10_000 });
        } finally {
            isSubmittingRef.current = false;
        }
    }, [createEnvironment, selectedClassEntry?.clazz?.id, onClose, navigate, toast]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const runnerID = selectedClassEntry?.runner?.runnerId;
            const classID = selectedClassEntry?.clazz?.id;
            if (!runnerID || !classID) {
                console.log(`Invalid runnerId=${runnerID} or classID=${classID}`);
                return;
            }

            await handleContinue();
        },
        [handleContinue, selectedClassEntry],
    );

    const createButtonDisabled = selectedClassEntry === undefined || !!createError || createEnvironment.isSuccess;

    return (
        <form onSubmit={handleSubmit} className="pt-6">
            <div className="flex flex-col gap-2" id="create-environment-form">
                <EnvironmentClassSelect
                    label="Environment class"
                    value={selectedClassEntry}
                    onChange={(v) => {
                        setSelectedClassEntry(v);
                        setCreateError(null);
                    }}
                    name="environment-class"
                    loading={isLoadingEnvClasses}
                    disabled={false}
                    environmentClassEntries={environmentClassEntries}
                />

                <div data-testid="errors" className="flex min-h-14 flex-col items-center justify-center gap-2 text-sm">
                    {createError && <EnvironmentCreateError error={createError} />}
                </div>

                <div className="flex flex-col gap-2">
                    <Button
                        type="submit"
                        variant="primary"
                        className="w-full text-base font-bold"
                        loading={createEnvironment.isPending}
                        disabled={createButtonDisabled}
                        data-testid="create-environment-button"
                        data-tracking-id="create-environment-from-scratch-blank-tab"
                    >
                        Create
                    </Button>
                </div>
            </div>
        </form>
    );
};
